// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/warehouse/TransfersPage.tsx
//
// تغییرات این نسخه:
// - فرم جدید: تعدیل گروهی (Bulk Adjustment) مثل QuickBooks
// - چندین ردیف داینامیک با تعدیل مثبت/منفی برای هر ردیف
// - اعتبارسنجی: همه ردیف‌ها باید از یک دسته (category) باشند
// - هشدار: اگر مجموع منفی‌ها با مثبت‌ها برابر نباشد
// - الگوریتم زوج‌بندی هوشمند در backend
// - لیست به‌جای تک‌تک رکوردها، گروه‌ها را نشان می‌دهد
// - حذف کل گروه با یک کلیک (با rollback خودکار موجودی)
// ============================================================

import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { warehouseAPI } from "../../services/api";
import { Plus, Save, X, Trash2, AlertTriangle, CheckCircle2, Eye } from "lucide-react";
import PersianDatePicker, { todayGregorian } from "../../utils/PersianDatePicker";
import { gregorianToJalaliStr } from "../../utils/PersianDatePicker";

const fmt = (n: any) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(Number(n) || 0);

interface AdjustRow {
  product_id: string;
  warehouse_id: string;
  adjustment: string; // می‌تواند مثبت یا منفی باشد
}

const emptyRow = (): AdjustRow => ({ product_id: "", warehouse_id: "", adjustment: "" });

export default function TransfersPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  
  const [bulks, setBulks] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [viewBulk, setViewBulk] = useState<any>(null); // برای نمایش جزئیات گروه
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [filters, setFilters] = useState({ from: "", to: "", warehouse_id: "", product_id: "", search: "" });
  
  // فرم تعدیل گروهی
  const [date, setDate] = useState<string>(todayGregorian());
  const [description, setDescription] = useState<string>("");
  const [truckPlate, setTruckPlate] = useState<string>("");        // 🆕 شماره موتر
  const [waybillNumber, setWaybillNumber] = useState<string>("");  // 🆕 شماره بارنامه
  const [rows, setRows] = useState<AdjustRow[]>([emptyRow(), emptyRow()]);
  
  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const load = async () => {
    // هر API را جداگانه فراخوانی کن تا اگر یکی خطا داد، بقیه کار کنند
    try {
      const rb = await warehouseAPI.transferBulkList(filters);
      if (rb.success) setBulks(rb.bulks || []);
    } catch (e) { console.error("transferBulkList error:", e); }
    
    try {
      const rp = await warehouseAPI.productsList({ status: "active" });
      if (rp.success) setProducts(rp.products);
    } catch (e) { console.error("productsList error:", e); }
    
    try {
      const rw = await warehouseAPI.warehousesList();
      if (rw.success) setWarehouses(rw.warehouses);
    } catch (e) { console.error("warehousesList error:", e); }
  };

  useEffect(() => { load(); }, [filters]);

  // ---------- استایل‌ها ----------
  const inp: React.CSSProperties = {
    width: "100%",
    padding: "8px 10px",
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 8,
    color: T.text,
    fontSize: 12,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl" as const,
    outline: "none",
    boxSizing: "border-box" as const,
  };
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 4 };
  const card: React.CSSProperties = {
    borderRadius: 14,
    padding: 20,
    background: dark ? "rgba(255,255,255,0.03)" : "#fff",
    border: `1px solid ${T.cardBorder}`,
    marginBottom: 14,
  };

  // ---------- منطق ردیف‌ها ----------
  const addRow = () => setRows([...rows, emptyRow()]);
  const removeRow = (idx: number) => {
    if (rows.length <= 2) {
      showToast("حداقل دو ردیف لازم است", false);
      return;
    }
    setRows(rows.filter((_, i) => i !== idx));
  };
  const updateRow = (idx: number, field: keyof AdjustRow, value: string) => {
    const newRows = [...rows];
    newRows[idx] = { ...newRows[idx], [field]: value };
    setRows(newRows);
  };

  // محاسبه موجودی فعلی یک ردیف
  const getCurrentStock = (productId: string, warehouseId: string): number => {
    if (!productId || !warehouseId) return 0;
    const p = products.find((x: any) => String(x.id) === productId);
    if (!p?.stock_by_warehouse) return 0;
    const s = p.stock_by_warehouse.find((s: any) => s.warehouse_id === Number(warehouseId));
    return s ? Number(s.quantity) : 0;
  };

  // بررسی یکسان بودن دسته همه ردیف‌ها
  const getCategoryStatus = (): { allSame: boolean; categoryName: string } => {
    const cats = rows
      .filter(r => r.product_id)
      .map(r => {
        const p = products.find((x: any) => String(x.id) === r.product_id);
        return p?.category_id;
      })
      .filter(c => c !== undefined && c !== null);
    
    const unique = Array.from(new Set(cats));
    if (unique.length <= 1) {
      const cat = cats[0];
      const p = products.find((x: any) => x.category_id === cat);
      return { allSame: true, categoryName: p?.category_name || "" };
    }
    return { allSame: false, categoryName: "" };
  };

  // محاسبه مجموع تعدیل
  const totalAdjustment = rows.reduce((sum, r) => {
    const v = parseFloat(r.adjustment);
    return sum + (isNaN(v) ? 0 : v);
  }, 0);

  // ریست فرم
  const resetForm = () => {
    setRows([emptyRow(), emptyRow()]);
    setDescription("");
    setTruckPlate("");       // 🆕
    setWaybillNumber("");    // 🆕
    setDate(todayGregorian());
  };

  // ---------- ثبت تعدیل گروهی ----------
  const handleSubmit = async () => {
    // اعتبارسنجی پایه
    const validRows = rows.filter(r => r.product_id && r.warehouse_id && parseFloat(r.adjustment) !== 0);
    if (validRows.length < 2) {
      showToast("حداقل دو ردیف معتبر لازم است", false);
      return;
    }

    // چک یکسان بودن دسته
    const catStatus = getCategoryStatus();
    if (!catStatus.allSame) {
      showToast("همه ردیف‌ها باید از یک دسته باشند", false);
      return;
    }

    // هشدار اگر موازنه نباشد (ولی اجازه ثبت می‌دهیم)
    if (Math.abs(totalAdjustment) > 0.0001) {
      const proceed = window.confirm(
        `⚠️ هشدار: مجموع تعدیل = ${fmt(totalAdjustment)} (موازنه نیست)\n\n` +
        `این به این معنی است که مجموع منفی‌ها با مجموع مثبت‌ها برابر نیست. ` +
        `مازاد به‌عنوان "تعدیل دستی" ثبت می‌شود.\n\n` +
        `آیا با این وضع ادامه دهیم؟`
      );
      if (!proceed) return;
    }

    const payload = {
      date,
      description,
      truck_plate: truckPlate,         // 🆕 شماره موتر
      waybill_number: waybillNumber,   // 🆕 شماره بارنامه
      items: validRows.map(r => ({
        product_id: parseInt(r.product_id),
        warehouse_id: parseInt(r.warehouse_id),
        adjustment: parseFloat(r.adjustment),
      })),
    };

    const r = await warehouseAPI.transferBulkAdd(payload);
    if (r.success) {
      const msg = `✅ تعدیل ${r.bulk_id} ثبت شد (${r.transfers_created} انتقال + ${r.transactions_created} تعدیل)`;
      showToast(msg);
      setShowAdd(false);
      resetForm();
      load();
    } else {
      showToast(r.message || "خطا در ثبت", false);
    }
  };

  // ---------- حذف گروه ----------
  const handleDeleteBulk = async (bulkId: string) => {
    if (!window.confirm(`⚠️ آیا مطمئنید می‌خواهید سند ${bulkId} را حذف کنید؟\n\nهمه رکوردهای این گروه حذف و موجودی‌ها برگردانده می‌شوند.`)) return;
    const r = await warehouseAPI.transferBulkDelete(bulkId);
    if (r.success) {
      showToast(`✅ سند ${bulkId} حذف شد`);
      load();
    } else {
      showToast(r.message || "خطا", false);
    }
  };

  // ---------- نمایش جزئیات یک گروه ----------
  const handleViewBulk = async (bulkId: string) => {
    const r = await warehouseAPI.transferBulkGet(bulkId);
    if (r.success) {
      setViewBulk(r);
    } else {
      showToast(r.message || "خطا", false);
    }
  };

  const catStatus = getCategoryStatus();

  return (
    <div dir="rtl">
      {toast && (
        <div style={{
          position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999,
          padding: "12px 24px", borderRadius: 12,
          background: dark ? "#1e293b" : "#fff",
          border: `2px solid ${toast.ok ? "#10b981" : "#ef4444"}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          color: toast.ok ? "#10b981" : "#ef4444",
          fontSize: 13, fontWeight: 700, fontFamily: "inherit",
        }}>{toast.msg}</div>
      )}

      {/* ============ فرم تعدیل گروهی ============ */}
      {showAdd && (
        <div style={{ ...card, borderRight: "4px solid #8b5cf6", marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>
            🔄 تعدیل/انتقال گروهی جنس‌ها
          </div>

          {/* تاریخ و شرح */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 3fr", gap: 12, marginBottom: 12 }}>
            <div>
              <PersianDatePicker label="تاریخ *" value={date} onChange={v => setDate(v)} />
            </div>
            <div>
              <label style={lbl}>شرح کلی سند</label>
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder="توضیحات این تعدیل گروهی..." style={inp} />
            </div>
          </div>

          {/* 🆕 شماره موتر و شماره بارنامه — چیدمان لوکس */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 16,
            padding: "12px 14px",
            borderRadius: 10,
            background: dark ? "rgba(139,92,246,0.06)" : "rgba(139,92,246,0.04)",
            border: `1px dashed ${dark ? "rgba(139,92,246,0.25)" : "rgba(139,92,246,0.3)"}`,
          }}>
            <div>
              <label style={{ ...lbl, color: "#8b5cf6", display: "flex", alignItems: "center", gap: 5 }}>
                <span>🚚</span> شماره موتر
              </label>
              <input
                value={truckPlate}
                onChange={e => setTruckPlate(e.target.value)}
                placeholder="مثلاً: 12345"
                style={{ ...inp, direction: "ltr" as const, fontWeight: 600 }}
              />
            </div>
            <div>
              <label style={{ ...lbl, color: "#8b5cf6", display: "flex", alignItems: "center", gap: 5 }}>
                <span>📄</span> شماره بارنامه
              </label>
              <input
                value={waybillNumber}
                onChange={e => setWaybillNumber(e.target.value)}
                placeholder="شماره بارنامه..."
                style={{ ...inp, direction: "ltr" as const, fontWeight: 600 }}
              />
            </div>
          </div>

          {/* جدول ردیف‌ها */}
          <div style={{
            borderRadius: 10, overflow: "hidden",
            border: `1px solid ${T.cardBorder}`,
            background: dark ? "rgba(0,0,0,0.15)" : "#fafbff",
            marginBottom: 14,
          }}>
            {/* هدر جدول */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "30px 2fr 1.5fr 90px 100px 90px 32px",
              gap: 8, padding: "10px 12px",
              background: dark ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.06)",
              borderBottom: `1px solid ${T.cardBorder}`,
              fontSize: 11, fontWeight: 700, color: T.subtext,
            }}>
              <span>#</span>
              <span>جنس *</span>
              <span>گدام *</span>
              <span style={{ textAlign: "center" }}>موجودی</span>
              <span style={{ textAlign: "center", color: "#8b5cf6" }}>تعدیل *</span>
              <span style={{ textAlign: "center" }}>نهایی</span>
              <span></span>
            </div>

            {/* ردیف‌ها */}
            {rows.map((row, idx) => {
              const stock = getCurrentStock(row.product_id, row.warehouse_id);
              const adj = parseFloat(row.adjustment) || 0;
              const final = stock + adj;
              return (
                <div key={idx} style={{
                  display: "grid",
                  gridTemplateColumns: "30px 2fr 1.5fr 90px 100px 90px 32px",
                  gap: 8, padding: "10px 12px",
                  borderBottom: `1px solid ${T.cardBorder}`,
                  alignItems: "center",
                  background: idx % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)"),
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.subtext }}>{idx + 1}</span>
                  <select
                    value={row.product_id}
                    onChange={e => updateRow(idx, "product_id", e.target.value)}
                    style={{ ...inp, background: dark ? "#1e293b" : "#fff" }}
                  >
                    <option value="">— انتخاب جنس</option>
                    {products.map((p: any) => (
                      <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                    ))}
                  </select>
                  <select
                    value={row.warehouse_id}
                    onChange={e => updateRow(idx, "warehouse_id", e.target.value)}
                    style={{ ...inp, background: dark ? "#1e293b" : "#fff" }}
                  >
                    <option value="">— انتخاب گدام</option>
                    {warehouses.map((w: any) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                  <span style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: stock < 0 ? "#ef4444" : T.text }}>
                    {row.product_id && row.warehouse_id ? fmt(stock) : "—"}
                  </span>
                  <input
                    type="number"
                    value={row.adjustment}
                    onChange={e => updateRow(idx, "adjustment", e.target.value)}
                    placeholder="+/-"
                    style={{
                      ...inp,
                      direction: "ltr" as const,
                      textAlign: "center",
                      fontWeight: 700,
                      color: adj > 0 ? "#10b981" : adj < 0 ? "#ef4444" : T.text,
                    }}
                  />
                  <span style={{
                    textAlign: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    color: final < 0 ? "#ef4444" : final === 0 ? T.subtext : "#10b981",
                  }}>
                    {row.product_id && row.warehouse_id ? fmt(final) : "—"}
                  </span>
                  <button
                    onClick={() => removeRow(idx)}
                    title="حذف ردیف"
                    style={{
                      width: 28, height: 28, borderRadius: 6,
                      border: "1px solid #ef444450",
                      background: "transparent",
                      color: "#ef4444",
                      cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}

            {/* خلاصه و دکمه افزودن */}
            <div style={{
              padding: "12px",
              background: dark ? "rgba(139,92,246,0.05)" : "rgba(139,92,246,0.03)",
              borderTop: `1px solid ${T.cardBorder}`,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <button onClick={addRow} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 14px", borderRadius: 8,
                border: "1px dashed #8b5cf6",
                background: "transparent",
                color: "#8b5cf6",
                cursor: "pointer",
                fontSize: 12, fontFamily: "inherit", fontWeight: 700,
              }}>
                <Plus size={12} /> افزودن ردیف
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 11, color: T.subtext }}>مجموع تعدیل:</span>
                <span style={{
                  fontSize: 14, fontWeight: 800,
                  color: Math.abs(totalAdjustment) < 0.0001 ? "#10b981" : "#f59e0b",
                  padding: "4px 10px", borderRadius: 6,
                  background: Math.abs(totalAdjustment) < 0.0001 ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)",
                  direction: "ltr" as const,
                }}>
                  {totalAdjustment > 0 ? "+" : ""}{fmt(totalAdjustment)}
                </span>
                {Math.abs(totalAdjustment) < 0.0001 ? (
                  <CheckCircle2 size={14} color="#10b981" />
                ) : (
                  <AlertTriangle size={14} color="#f59e0b" />
                )}
              </div>
            </div>
          </div>

          {/* اطلاع‌رسانی دسته */}
          {rows.some(r => r.product_id) && (
            <div style={{
              padding: "10px 14px", borderRadius: 10, marginBottom: 14,
              background: catStatus.allSame ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
              border: `1px solid ${catStatus.allSame ? "#10b98140" : "#ef444440"}`,
              fontSize: 12, color: catStatus.allSame ? "#10b981" : "#ef4444",
              fontWeight: 700,
            }}>
              {catStatus.allSame ? (
                <>✅ همه ردیف‌ها از دسته «{catStatus.categoryName}» هستند</>
              ) : (
                <>❌ ردیف‌ها از دسته‌های متفاوت هستند - باید همه از یک دسته باشند</>
              )}
            </div>
          )}

          {/* دکمه‌ها */}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={() => { setShowAdd(false); resetForm(); }} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "9px 16px", borderRadius: 9,
              border: `1px solid ${T.cardBorder}`,
              background: "transparent",
              color: T.text,
              cursor: "pointer",
              fontSize: 13, fontFamily: "inherit",
            }}>
              <X size={13} /> انصراف
            </button>
            <button onClick={handleSubmit} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "9px 18px", borderRadius: 9,
              border: "none",
              background: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
              color: "white",
              cursor: "pointer",
              fontSize: 13, fontFamily: "inherit", fontWeight: 700,
            }}>
              <Save size={13} /> ثبت تعدیل
            </button>
          </div>
        </div>
      )}

      {/* ============ فیلترهای حرفه‌ای ============ */}
      <div style={{
        ...card,
        marginBottom: 16,
        background: dark ? "rgba(255,255,255,0.03)" : "linear-gradient(135deg,#fff,#fafbff)",
        border: `1px solid ${T.cardBorder}`,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
          <div>
            <label style={lbl}>🔍 جستجوی هوشمند</label>
            <input
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              placeholder="شناسه سند، شرح، موتر، بارنامه..."
              style={inp}
            />
          </div>
          <div>
            <label style={lbl}>🏭 گدام</label>
            <select
              value={filters.warehouse_id}
              onChange={e => setFilters(f => ({ ...f, warehouse_id: e.target.value }))}
              style={{ ...inp, background: dark ? "#1e293b" : "#fff" }}
            >
              <option value="">— همه گدام‌ها —</option>
              {warehouses.map((w: any) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>📦 جنس</label>
            <select
              value={filters.product_id}
              onChange={e => setFilters(f => ({ ...f, product_id: e.target.value }))}
              style={{ ...inp, background: dark ? "#1e293b" : "#fff" }}
            >
              <option value="">— همه اجناس —</option>
              {products.map((p: any) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div><PersianDatePicker label="از تاریخ" value={filters.from} onChange={v => setFilters(f => ({ ...f, from: v }))} /></div>
          <div><PersianDatePicker label="تا تاریخ" value={filters.to} onChange={v => setFilters(f => ({ ...f, to: v }))} /></div>
          {!showAdd && (
            <button onClick={() => setShowAdd(true)} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "9px 16px", borderRadius: 9,
              border: "none",
              background: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
              color: "white",
              cursor: "pointer",
              fontSize: 13, fontFamily: "inherit", fontWeight: 700,
              whiteSpace: "nowrap",
              boxShadow: "0 4px 12px rgba(139,92,246,0.4)",
            }}>
              <Plus size={14} /> تعدیل جدید
            </button>
          )}
        </div>
      </div>

      {/* ============ لیست گروه‌ها — لوکس با اسکرول ============ */}
      {(() => {
        // فیلتر کلاینت‌ساید
        const filteredBulks = bulks.filter(b => {
          if (filters.search) {
            const q = filters.search.toLowerCase();
            const match = (b.bulk_id || "").toLowerCase().includes(q)
                       || (b.description || "").toLowerCase().includes(q)
                       || (b.truck_plate || "").toLowerCase().includes(q)
                       || (b.waybill_number || "").toLowerCase().includes(q);
            if (!match) return false;
          }
          return true;
        });
        // 50 تای آخر
        const visibleBulks = filteredBulks.slice(0, 50);

        return (
          <div style={{
            borderRadius: 16,
            overflow: "hidden",
            border: `1px solid ${T.cardBorder}`,
            background: dark ? "rgba(255,255,255,0.02)" : "#fff",
            boxShadow: dark ? "none" : "0 4px 20px rgba(0,0,0,0.04)",
          }}>
            {/* هدر اطلاعاتی جدول */}
            <div style={{
              padding: "12px 18px",
              background: dark
                ? "linear-gradient(135deg,rgba(139,92,246,0.15),rgba(124,58,237,0.08))"
                : "linear-gradient(135deg,#f5f3ff,#ede9fe)",
              borderBottom: `1px solid ${T.cardBorder}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 16, boxShadow: "0 4px 10px rgba(139,92,246,0.3)",
                }}>📋</div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>تاریخچه انتقالات و تعدیلات</div>
                  <div style={{ fontSize: 11, color: T.subtext, marginTop: 2 }}>
                    نمایش {visibleBulks.length} از {filteredBulks.length} سند
                    {filteredBulks.length < bulks.length && ` (از کل ${bulks.length})`}
                  </div>
                </div>
              </div>
              <div style={{
                fontSize: 11, fontWeight: 700, color: "#8b5cf6",
                padding: "5px 12px", borderRadius: 8,
                background: dark ? "rgba(139,92,246,0.15)" : "rgba(139,92,246,0.1)",
                border: "1px solid rgba(139,92,246,0.25)",
              }}>
                📜 50 سند آخر
              </div>
            </div>

            {/* هدر ستون‌ها */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "100px 130px 1fr 110px 120px 75px 75px 100px",
              gap: 8, padding: "12px 18px",
              background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)",
              borderBottom: `2px solid ${dark ? "rgba(139,92,246,0.2)" : "#ede9fe"}`,
              fontSize: 11, fontWeight: 800, color: T.subtext,
              position: "sticky", top: 0, zIndex: 2,
            }}>
              <span>📅 تاریخ</span>
              <span>🔖 شناسه سند</span>
              <span>📝 شرح</span>
              <span>🚚 شماره موتر</span>
              <span>📄 شماره بارنامه</span>
              <span style={{ textAlign: "center" }}>انتقال</span>
              <span style={{ textAlign: "center" }}>تعدیل</span>
              <span style={{ textAlign: "center" }}>عملیات</span>
            </div>

            {/* بدنه جدول — قابل اسکرول */}
            <div style={{
              maxHeight: 600,
              overflowY: "auto" as const,
              scrollbarWidth: "thin" as const,
            }}>
              {visibleBulks.length === 0 ? (
                <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>🔄</div>
                  هیچ سندی یافت نشد
                </div>
              ) : (
                visibleBulks.map((b, i) => (
                  <div key={b.bulk_id} style={{
                    display: "grid",
                    gridTemplateColumns: "100px 130px 1fr 110px 120px 75px 75px 100px",
                    gap: 8, padding: "13px 18px",
                    borderBottom: `1px solid ${T.cardBorder}`,
                    alignItems: "center",
                    background: i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "rgba(139,92,246,0.025)"),
                    transition: "background 0.15s",
                    cursor: "default",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = dark ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.07)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,0.015)" : "rgba(139,92,246,0.025)");
                  }}
                  >
                    <span style={{ fontSize: 11, color: T.subtext, direction: "ltr" as const, fontWeight: 600 }}>
                      {b.date ? gregorianToJalaliStr(b.date) : "—"}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 800, color: "#8b5cf6", direction: "ltr" as const,
                      padding: "3px 8px", borderRadius: 6,
                      background: dark ? "rgba(139,92,246,0.15)" : "rgba(139,92,246,0.1)",
                      display: "inline-block", width: "fit-content",
                    }}>
                      {b.bulk_id}
                    </span>
                    <span style={{ fontSize: 12, color: T.text }}>
                      {b.description || "—"}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: b.truck_plate ? "#3b82f6" : T.subtext, direction: "ltr" as const, fontFamily: b.truck_plate ? "monospace" : "inherit" }}>
                      {b.truck_plate ? `🚚 ${b.truck_plate}` : "—"}
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: b.waybill_number ? "#0ea5e9" : T.subtext, direction: "ltr" as const, fontFamily: b.waybill_number ? "monospace" : "inherit" }}>
                      {b.waybill_number ? `📄 ${b.waybill_number}` : "—"}
                    </span>
                    <span style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: "#10b981" }}>
                      {b.transfer_count > 0 ? `${b.transfer_count}× (${fmt(b.transfer_qty)})` : "—"}
                    </span>
                    <span style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: "#f59e0b" }}>
                      {b.transaction_count > 0 ? `${b.transaction_count}×` : "—"}
                    </span>
                    <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                      <button
                        onClick={() => handleViewBulk(b.bulk_id)}
                        title="مشاهده جزئیات"
                        style={{
                          padding: "5px 10px", borderRadius: 7,
                          border: "1px solid #3b82f6", background: "transparent",
                          color: "#3b82f6", cursor: "pointer", fontSize: 11,
                          transition: "all 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#3b82f6"; e.currentTarget.style.color = "#fff"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#3b82f6"; }}
                      >
                        <Eye size={11} />
                      </button>
                      <button
                        onClick={() => handleDeleteBulk(b.bulk_id)}
                        title="حذف کل گروه"
                        style={{
                          padding: "5px 10px", borderRadius: 7,
                          border: "1px solid #ef4444", background: "transparent",
                          color: "#ef4444", cursor: "pointer", fontSize: 11,
                          transition: "all 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#ef4444"; e.currentTarget.style.color = "#fff"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#ef4444"; }}
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })()}

      {/* ============ مودال جزئیات گروه ============ */}
      {viewBulk && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setViewBulk(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            dir="rtl"
            style={{
              background: dark ? "#1e293b" : "#fff",
              borderRadius: 16, padding: 28, maxWidth: 800, width: "100%", maxHeight: "85vh", overflow: "auto",
              border: `1px solid ${T.cardBorder}`,
              boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
              fontFamily: "inherit",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>📄 جزئیات سند: {viewBulk.bulk_id}</div>
              </div>
              <button onClick={() => setViewBulk(null)} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "transparent", color: T.subtext, cursor: "pointer" }}>
                <X size={16} />
              </button>
            </div>

            {/* 🆕 نمایش شماره موتر و بارنامه (اگر وجود داشته باشد) */}
            {(viewBulk.truck_plate || viewBulk.waybill_number) && (
              <div style={{
                display: "flex", gap: 12, marginBottom: 16, padding: "10px 14px",
                borderRadius: 10,
                background: dark ? "rgba(139,92,246,0.06)" : "rgba(139,92,246,0.04)",
                border: `1px solid ${dark ? "rgba(139,92,246,0.25)" : "rgba(139,92,246,0.2)"}`,
              }}>
                {viewBulk.truck_plate && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.subtext }}>🚚 شماره موتر:</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#8b5cf6", direction: "ltr" as const }}>
                      {viewBulk.truck_plate}
                    </span>
                  </div>
                )}
                {viewBulk.waybill_number && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.subtext }}>📄 شماره بارنامه:</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color: "#0ea5e9", direction: "ltr" as const }}>
                      {viewBulk.waybill_number}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* بخش انتقال‌ها */}
            {viewBulk.transfers && viewBulk.transfers.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#10b981", marginBottom: 8 }}>
                  🔄 انتقال‌های گدام به گدام ({viewBulk.transfers.length} ردیف)
                </div>
                <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${T.cardBorder}` }}>
                  <div style={{
                    display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 100px",
                    gap: 8, padding: "8px 12px",
                    background: dark ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.05)",
                    fontSize: 11, fontWeight: 700, color: T.subtext,
                  }}>
                    <span>جنس مبدا</span>
                    <span>گدام مبدا</span>
                    <span>جنس مقصد</span>
                    <span>گدام مقصد</span>
                    <span style={{ textAlign: "center" }}>مقدار</span>
                  </div>
                  {viewBulk.transfers.map((t: any) => (
                    <div key={t.id} style={{
                      display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 100px",
                      gap: 8, padding: "10px 12px",
                      borderTop: `1px solid ${T.cardBorder}`,
                      fontSize: 12, color: T.text,
                    }}>
                      <span>{t.from_product_name}</span>
                      <span style={{ color: "#ef4444" }}>{t.from_warehouse_name}</span>
                      <span>{t.to_product_name}</span>
                      <span style={{ color: "#10b981" }}>{t.to_warehouse_name}</span>
                      <span style={{ textAlign: "center", fontWeight: 700, color: "#8b5cf6" }}>{fmt(t.quantity)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* بخش تعدیل‌ها */}
            {viewBulk.transactions && viewBulk.transactions.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#f59e0b", marginBottom: 8 }}>
                  📝 تعدیل‌های دستی - مازاد ({viewBulk.transactions.length} ردیف)
                </div>
                <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${T.cardBorder}` }}>
                  <div style={{
                    display: "grid", gridTemplateColumns: "1.5fr 1fr 80px 100px",
                    gap: 8, padding: "8px 12px",
                    background: dark ? "rgba(245,158,11,0.08)" : "rgba(245,158,11,0.05)",
                    fontSize: 11, fontWeight: 700, color: T.subtext,
                  }}>
                    <span>جنس</span>
                    <span>گدام</span>
                    <span style={{ textAlign: "center" }}>نوع</span>
                    <span style={{ textAlign: "center" }}>مقدار</span>
                  </div>
                  {viewBulk.transactions.map((t: any) => (
                    <div key={t.id} style={{
                      display: "grid", gridTemplateColumns: "1.5fr 1fr 80px 100px",
                      gap: 8, padding: "10px 12px",
                      borderTop: `1px solid ${T.cardBorder}`,
                      fontSize: 12, color: T.text,
                    }}>
                      <span>{t.product_name}</span>
                      <span>{t.warehouse_name}</span>
                      <span style={{
                        textAlign: "center",
                        fontWeight: 700,
                        color: t.type === "in" ? "#10b981" : "#ef4444",
                      }}>
                        {t.type === "in" ? "ورود" : "خروج"}
                      </span>
                      <span style={{
                        textAlign: "center",
                        fontWeight: 700,
                        color: t.type === "in" ? "#10b981" : "#ef4444",
                        direction: "ltr" as const,
                      }}>
                        {t.type === "in" ? "+" : "-"}{fmt(t.quantity)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 18, display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setViewBulk(null)} style={{
                padding: "9px 18px", borderRadius: 9, border: "none",
                background: "linear-gradient(135deg,#3b82f6,#2563eb)",
                color: "white", cursor: "pointer",
                fontSize: 13, fontFamily: "inherit", fontWeight: 700,
              }}>بستن</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}